import { useCallback, useEffect, useMemo, useState } from "react";
import { CommonActions, useNavigation } from "@react-navigation/native";
import {
  View,
  Text,
  Pressable,
  Image,
  Linking,
  NativeScrollEvent,
  NativeSyntheticEvent,
  type ImageSourcePropType,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  Heart,
  Wrench,
  Clock,
  DollarSign,
  CalendarDays,
  Info,
  CheckCircle,
  Lightbulb,
  ExternalLink,
  Beaker,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  XCircle,
  AlertCircle,
  Target,
  BookOpen,
  FileText,
  ChevronLeft,
  Globe2,
  Tags,
} from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import type {
  ContentBlock,
  ContentBlockId,
  DataPointBody,
  TestBody,
  TestMarkerCategory,
  ProductBody,
} from "@src/packages/content";
import {
  getBlock,
  getDataPointsForProduct,
  getDataPointsForTest,
} from "@src/packages/content";
import {
  FactGrid,
  CardSubtitleText,
  CardSurface,
  cardSurface,
  CloseButton,
  IconButton,
  useScrollHaptics,
  springs,
  SectionTitle,
  PrimaryButton,
  DisclaimerNote,
  MetaText,
  MoonDetailRow,
  Tag,
  type TagTone,
} from "@louise/moon-ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import {
  useTranslations,
  useLocale,
  type TranslationFunction,
} from "@src/packages/i18n";
import EvidenceLevelModal from "./EvidenceLevelModal";
import EvidenceSourceRows from "./EvidenceSourceRows";
import { getTestRouteTarget } from "../screens/access/testRouteIds";
import { EMBER } from "../../onboarding/theme";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import OrderKitButton from "./OrderKitButton";
import { getLocalProductImage } from "./productImages";
import FactSheetHeader, {
  type FactSheetLink,
  type FactSheetTagItem,
} from "./FactSheetHeader";
import {
  dataPointMetaLine,
  hasSampleQuickFact,
  soldByFact,
  testQuickFactsAsGlanceFacts,
  whereTestIsDoneFact,
  type TestGlanceFact,
} from "./testGlanceFacts";
import LinkedFactSheetText, {
  type FactSheetLinkTarget,
} from "./LinkedFactSheetText";
import { flagFromIso } from "./countryFlag";
import type { MainTabParamList } from "../types";
import { getTestIllustration } from "./access/testIllustrations";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors, withAlpha } from "@louise/theme/tokens";
// Maps the raw category value from CMS to a stable i18n key (learn.test_detail.category_*).
const CATEGORY_I18N_KEYS: Record<string, string> = {
  "blood-marker": "category_blood_marker",
  imaging: "category_imaging",
  genetic: "category_genetic",
  semen: "category_semen",
  monitoring: "category_monitoring",
  biopsy: "category_biopsy",
};

// Categories that only say what the sample is, so the At a glance grid's
// Sample fact already states them.
const CATEGORIES_STATED_BY_SAMPLE = new Set<TestMarkerCategory>([
  "blood-marker",
  "imaging",
  "semen",
  "monitoring",
]);

const TEST_ILLUSTRATIONS: Record<string, ImageSourcePropType> = {
  "test-chronic-endometritis-testing": require("../../assets/images/therapies/electron-microscope.png"),
};

const TEST_RESULT_TYPE_ILLUSTRATIONS: Record<
  string,
  Partial<Record<string, ImageSourcePropType>>
> = {
  "test-pgt-a": {
    Euploid: require("../../assets/images/testing/pgt-a-euploid.png"),
    Aneuploid: require("../../assets/images/testing/pgt-a-aneuploid.png"),
    Mosaic: require("../../assets/images/testing/pgt-a-mosaic.png"),
    "No-result": require("../../assets/images/testing/pgt-a-no-result.png"),
  },
};

type ResultGlyphKind =
  | "window-receptive"
  | "window-pre"
  | "window-post"
  | "microbiome-dominant"
  | "microbiome-dysbiotic"
  | "microbiome-pathogen"
  | "microbiome-low"
  | "genetic-clear"
  | "genetic-carrier"
  | "genetic-affected"
  | "genetic-pair-risk"
  | "chromosome-typical"
  | "chromosome-balanced"
  | "chromosome-unbalanced"
  | "chromosome-mosaic"
  | "unknown";

const TEST_RESULT_TYPE_GLYPHS: Record<
  string,
  Partial<Record<string, ResultGlyphKind>>
> = {
  "test-endometrial-receptivity-testing": {
    Receptive: "window-receptive",
    "Pre-receptive": "window-pre",
    "Post-receptive": "window-post",
    Inconclusive: "unknown",
  },
  "test-endometrial-receptivity-testing-products-era": {
    Receptive: "window-receptive",
    "Pre-receptive": "window-pre",
    "Post-receptive": "window-post",
    Inconclusive: "unknown",
  },
  "test-endometrial-receptivity-testing-products-erpeak": {
    Receptive: "window-receptive",
    "Pre-receptive": "window-pre",
    "Post-receptive": "window-post",
    Inconclusive: "unknown",
  },
  "test-endometrial-receptivity-testing-products-beready": {
    Receptive: "window-receptive",
    "Pre-receptive": "window-pre",
    "Post-receptive": "window-post",
    Inconclusive: "unknown",
  },
  "test-endometrial-microbiome-testing": {
    "Lactobacillus-dominant": "microbiome-dominant",
    Dysbiotic: "microbiome-dysbiotic",
    "Pathogen-associated": "microbiome-pathogen",
    "Low biomass or inconclusive": "microbiome-low",
  },
  "test-endometrial-microbiome-testing-products-emma": {
    "Lactobacillus-dominant": "microbiome-dominant",
    Dysbiotic: "microbiome-dysbiotic",
    "Pathogen-associated": "microbiome-pathogen",
    "Low biomass or inconclusive": "microbiome-low",
  },
  "test-endometrial-microbiome-testing-products-erbiome": {
    "Lactobacillus-dominant": "microbiome-dominant",
    Dysbiotic: "microbiome-dysbiotic",
    "Pathogen-associated": "microbiome-pathogen",
    "Low biomass or inconclusive": "microbiome-low",
  },
  "test-chronic-endometritis-testing": {
    "Not detected": "microbiome-dominant",
    Detected: "microbiome-pathogen",
    Inconclusive: "unknown",
  },
  "test-chronic-endometritis-testing-products-alice": {
    "Not detected": "microbiome-dominant",
    Detected: "microbiome-pathogen",
    Inconclusive: "unknown",
  },
  "test-endometrial-add-on-bundle": {
    "All components not flagged": "microbiome-dominant",
    "ERA non-receptive": "window-pre",
    "Microbiome or inflammation flagged": "microbiome-pathogen",
    "Mixed or inconclusive": "unknown",
  },
  "test-endometrial-add-on-bundle-products-endometrio": {
    "All components not flagged": "microbiome-dominant",
    "ERA non-receptive": "window-pre",
    "Microbiome or inflammation flagged": "microbiome-pathogen",
    "Mixed or inconclusive": "unknown",
  },
  "test-endometrial-add-on-bundle-products-er-complete": {
    "All components not flagged": "microbiome-dominant",
    "ERA non-receptive": "window-pre",
    "Microbiome or inflammation flagged": "microbiome-pathogen",
    "Mixed or inconclusive": "unknown",
  },
  "test-pgt-m": {
    Unaffected: "genetic-clear",
    Carrier: "genetic-carrier",
    "Affected or high-risk": "genetic-affected",
    "No-result": "unknown",
  },
  "test-pgt-sr": {
    "Normal or balanced": "chromosome-balanced",
    Unbalanced: "chromosome-unbalanced",
    "Mosaic or segmental": "chromosome-mosaic",
    "No-result": "unknown",
  },
  "test-pgt-p": {
    "Lower predicted risk": "genetic-clear",
    "Higher predicted risk": "genetic-affected",
    "Limited-use score": "unknown",
    "No-result": "unknown",
  },
  "test-carrier-screening": {
    "No carrier variant found": "genetic-clear",
    Carrier: "genetic-carrier",
    "Both partners carry the same condition": "genetic-pair-risk",
    "Uncertain or incomplete": "unknown",
  },
  "test-karyotype": {
    "Typical karyotype": "chromosome-typical",
    "Balanced rearrangement": "chromosome-balanced",
    "Unbalanced or extra/missing chromosome": "chromosome-unbalanced",
    "Mosaic or inconclusive": "chromosome-mosaic",
  },
};

const EVIDENCE_STRENGTH_KEYS: Record<
  NonNullable<TestBody["evidenceStrength"]>,
  string
> = {
  strong: "evidence_strength_strong",
  moderate: "evidence_strength_moderate",
  emerging: "evidence_strength_emerging",
  mixed: "evidence_strength_mixed",
  debunked: "evidence_strength_debunked",
};

const MINI_LABEL_TEXT_STYLE: TextStyle = {
  fontSize: 10,
  fontWeight: "700",
  textTransform: "uppercase",
  color: EMBER.colors.textFaint,
  letterSpacing: 0,
};

const COUNTRY_LABELS: Record<string, string> = {
  AE: "United Arab Emirates",
  AR: "Argentina",
  AT: "Austria",
  AU: "Australia",
  BE: "Belgium",
  BG: "Bulgaria",
  BR: "Brazil",
  CA: "Canada",
  CL: "Chile",
  CO: "Colombia",
  CY: "Cyprus",
  CZ: "Czechia",
  DE: "Germany",
  DK: "Denmark",
  EE: "Estonia",
  ES: "Spain",
  EU: "European Union",
  FI: "Finland",
  FR: "France",
  GB: "United Kingdom",
  GR: "Greece",
  HR: "Croatia",
  HU: "Hungary",
  IE: "Ireland",
  IN: "India",
  IT: "Italy",
  JP: "Japan",
  KR: "South Korea",
  LT: "Lithuania",
  LU: "Luxembourg",
  LV: "Latvia",
  MT: "Malta",
  MX: "Mexico",
  NL: "Netherlands",
  PE: "Peru",
  PL: "Poland",
  PT: "Portugal",
  RO: "Romania",
  SE: "Sweden",
  SI: "Slovenia",
  SK: "Slovakia",
  TR: "Turkey",
  TW: "Taiwan",
  US: "United States",
};

function getCountryLabel(iso: string, locale?: string): string {
  const code = iso.toUpperCase();
  if (typeof Intl !== "undefined" && Intl.DisplayNames) {
    try {
      const names = new Intl.DisplayNames([locale ?? "en"], { type: "region" });
      const localized = names.of(code);
      if (localized && localized !== code) return localized;
    } catch {
      /* fall through */
    }
  }
  return COUNTRY_LABELS[code] ?? code;
}

function getRelatedTestsLinkLabel(
  t: TranslationFunction,
  title?: string,
): string {
  if (!title) return t("learn.test_detail.view_all_related");
  const normalizedTitle = title.trim().toLowerCase();
  return t("learn.test_detail.view_all_prefix", {
    params: { category: normalizedTitle.replace(/\btesting$/, "tests") },
  });
}

/* ─── Detail Content ─── */

export function TestDetailContent({
  item,
  onClose,
  onBack,
  skipExitAnimation,
  onOpenMetric,
  onOpenFactSheet,
}: {
  item: ContentBlock<"test"> | ContentBlock<"product">;
  onClose: () => void;
  onBack?: () => void;
  skipExitAnimation?: boolean;
  onOpenMetric?: (metricKey: string) => void;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  const t = useTranslations();
  const insets = useSafeAreaInsets();
  const isProduct = item.kind === "product";
  const productBody = isProduct ? (item.body as ProductBody) : null;
  const localProductImage = isProduct
    ? getLocalProductImage(String(item.id))
    : undefined;
  const testBody = isProduct ? null : (item.body as unknown as TestBody);
  const testIllustration = !isProduct
    ? (TEST_ILLUSTRATIONS[item.id as string] ??
      getTestIllustration({
        testId: String(item.id),
        category: testBody?.category,
      }))
    : undefined;
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const onScrollHaptics = useScrollHaptics();
  const [evidenceModal, setEvidenceModal] = useState<string | null>(null);

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScrollHaptics(e);
    },
    [onScrollHaptics],
  );

  // Header tags, related-sheet links, and the facts the At a glance grid
  // adds to the content's quick facts.
  const tags: FactSheetTagItem[] = [];
  const links: FactSheetLink[] = [];
  const glanceFacts: TestGlanceFact[] = [];
  if (isProduct) {
    const productBody = item.body as ProductBody;
    for (const chip of productBody.chips ?? []) {
      tags.push({ kind: "feature", label: chip });
    }
  } else if (testBody) {
    const parentTest = testBody.parentTest;
    const openParentTest =
      onBack ??
      (parentTest && onOpenFactSheet
        ? () =>
            onOpenFactSheet({
              kind: "test",
              id: String(parentTest.testId),
            })
        : undefined);
    if (parentTest && openParentTest) {
      const parentBlock = getBlock(parentTest.testId);
      links.push({
        label:
          parentTest.badgeLabel ??
          getRelatedTestsLinkLabel(t, parentBlock?.title),
        action: "fact_sheet_open_related_tests",
        onPress: openParentTest,
      });
    }
    const commercialProfile = testBody.commercialProductProfile;
    const openCategoryTest =
      onBack ??
      (commercialProfile && onOpenFactSheet
        ? () =>
            onOpenFactSheet({
              kind: "test",
              id: String(commercialProfile.categoryTestId),
            })
        : undefined);
    if (commercialProfile && openCategoryTest) {
      const categoryBlock = getBlock(commercialProfile.categoryTestId);
      links.push({
        label: getRelatedTestsLinkLabel(t, categoryBlock?.title),
        action: "fact_sheet_open_related_tests",
        onPress: openCategoryTest,
      });
    }
    if (item.tags.includes("her")) {
      tags.push({ kind: "audience", audience: "her" });
    } else if (item.tags.includes("his")) {
      tags.push({ kind: "audience", audience: "him" });
    }
    if (
      testBody.category &&
      !(
        CATEGORIES_STATED_BY_SAMPLE.has(testBody.category) &&
        hasSampleQuickFact(testBody)
      )
    ) {
      const categoryKey = CATEGORY_I18N_KEYS[testBody.category];
      tags.push({
        kind: "category",
        label: categoryKey
          ? t(`learn.test_detail.${categoryKey}`)
          : testBody.category,
      });
    }
    if (testBody.atHome || testBody.clinicRequired) {
      glanceFacts.push(
        whereTestIsDoneFact(
          t,
          testBody.atHome
            ? testBody.clinicRequired
              ? "home-or-clinic"
              : "home"
            : "clinic",
        ),
      );
    }
    if (commercialProfile) {
      glanceFacts.push(soldByFact(t, commercialProfile.company));
    }
    if (testBody.evidenceStrength) {
      tags.push({
        kind: "evidence",
        label: t(
          `learn.test_detail.${EVIDENCE_STRENGTH_KEYS[testBody.evidenceStrength]}`,
        ),
        strength: testBody.evidenceStrength,
      });
    }
  }

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <CardSurface
              style={{
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {onBack ? (
                <View
                  style={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    zIndex: 2,
                  }}
                >
                  <IconButton
                    accessibilityLabel="Back"
                    action="back"
                    onPress={onBack}
                    hitSlop={8}
                    size={32}
                    surface="soft"
                    backgroundColor={semanticColors.surface.muted}
                    pressedBackgroundColor={semanticColors.overlay.pressed}
                    pressedOpacity={1}
                  >
                    <ChevronLeft size={17} color={EMBER.colors.textLight} />
                  </IconButton>
                </View>
              ) : null}

              {/* Close */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <CloseButton onPress={handleClose} />
              </View>

              <FactSheetHeader
                title={item.title}
                subtitle={
                  isProduct
                    ? item.subtitle
                    : item.subtitle ||
                      (testBody?.testName !== item.title
                        ? testBody?.testName
                        : undefined)
                }
                emoji={testIllustration ? undefined : item.icon}
                imageSource={
                  productBody?.imageUrl
                    ? { uri: productBody.imageUrl }
                    : testIllustration
                      ? testIllustration
                      : undefined
                }
                visual={
                  localProductImage ? (
                    <Image
                      source={localProductImage}
                      style={{ width: 248, height: 138 }}
                      resizeMode="contain"
                    />
                  ) : undefined
                }
                imageSize={testIllustration ? 72 : undefined}
                tags={tags}
                links={links}
              />

              {/* ── Content sections ── */}
              {isProduct ? (
                <ProductSections
                  body={item.body as ProductBody}
                  productId={item.id}
                />
              ) : (
                <TestSections
                  id={item.id}
                  body={item.body as unknown as TestBody}
                  sources={item.sources}
                  onEvidenceLevelPress={setEvidenceModal}
                  onOpenMetric={onOpenMetric}
                  onOpenFactSheet={onOpenFactSheet}
                  glanceFacts={glanceFacts}
                />
              )}

              {/* ── Disclaimer ── */}
              <DisclaimerNote style={{ marginTop: 32 }}>
                {t("learn.disclaimer_medical_info")}
              </DisclaimerNote>
            </CardSurface>
          </Animated.ScrollView>
        </View>
      </Animated.View>
      <EvidenceLevelModal
        level={evidenceModal}
        visible={evidenceModal !== null}
        onClose={() => setEvidenceModal(null)}
      />
    </View>
  );
}

function splitTestSteps(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((step) => step.trim())
    .filter(Boolean);
}

function TestStepRows({
  items,
  currentBlockId,
  onOpenFactSheet,
}: {
  items: string[];
  currentBlockId: string;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  if (!items.length) return null;

  return (
    <View style={{ marginTop: 10 }}>
      {items.map((item, i) => (
        <View
          key={`${item}-${i}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingTop: i === 0 ? 0 : 12,
            paddingBottom: i === items.length - 1 ? 0 : 12,
            borderTopWidth: i === 0 ? 0 : 1,
            borderTopColor: semanticColors.border.subtle,
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: withAlpha(semanticColors.text.secondary, 0.1),
              marginTop: 1,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: EMBER.colors.textMid,
              }}
            >
              {i + 1}
            </Text>
          </View>
          <LinkedFactSheetText
            text={item}
            currentBlockId={currentBlockId}
            onOpenLink={onOpenFactSheet}
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          />
        </View>
      ))}
    </View>
  );
}

function ReadableLinkedFactSheetText({
  text,
  currentBlockId,
  onOpenFactSheet,
  style,
}: {
  text: string;
  currentBlockId: string;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
  style?: StyleProp<TextStyle>;
}) {
  const paragraphs = text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (!paragraphs.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {paragraphs.map((paragraph, index) => (
        <LinkedFactSheetText
          key={`${paragraph.slice(0, 32)}-${index}`}
          text={paragraph}
          currentBlockId={currentBlockId}
          onOpenLink={onOpenFactSheet}
          style={style}
        />
      ))}
    </View>
  );
}

function BenefitRows({ items }: { items: string[] }) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((item, i) => (
        <View key={`${item}-${i}`} style={{ flexDirection: "row", gap: 10 }}>
          <CheckCircle
            size={13}
            color={EMBER.colors.green}
            strokeWidth={2}
            style={{ marginTop: 2.5 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function ResearchFindingRows({
  items,
}: {
  items: NonNullable<TestBody["keyFindings"]>;
}) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 16, marginTop: 10 }}>
      {items.map((finding, i) => {
        const DirectionIcon =
          finding.direction === "positive"
            ? TrendingUp
            : finding.direction === "negative"
              ? TrendingDown
              : Minus;
        const directionColor =
          finding.direction === "positive"
            ? semanticColors.status.success
            : finding.direction === "negative"
              ? semanticColors.status.danger
              : EMBER.colors.textLight;
        const directionTone: TagTone =
          finding.direction === "positive"
            ? "green"
            : finding.direction === "negative"
              ? "coral"
              : "neutral";

        return (
          <View key={`${finding.outcome}-${i}`}>
            <View style={{ flexDirection: "row", gap: 10 }}>
              <DirectionIcon
                size={12}
                color={directionColor}
                strokeWidth={2.5}
                style={{ marginTop: 4 }}
              />
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 14,
                  fontWeight: "600",
                  color: EMBER.colors.textMid,
                  lineHeight: 20,
                }}
              >
                {finding.outcome}
              </Text>
            </View>

            {finding.magnitude ? (
              <Tag
                label={finding.magnitude}
                tone={directionTone}
                style={{
                  marginTop: 8,
                  marginLeft: 22,
                  alignSelf: "flex-start",
                }}
              />
            ) : null}

            {finding.mechanism ? (
              <Text
                className="font-dm-sans"
                style={{
                  marginTop: 8,
                  marginLeft: 22,
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 19,
                }}
              >
                {finding.mechanism}
              </Text>
            ) : null}

            {finding.source ? (
              <Text
                className="font-dm-sans"
                style={{
                  marginTop: 6,
                  marginLeft: 22,
                  fontSize: 11,
                  color: EMBER.colors.textFaint,
                  lineHeight: 16,
                }}
              >
                {finding.source}
              </Text>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

type TestQuickFact = NonNullable<TestBody["quickFacts"]>[number];
type CommercialProductProfile = NonNullable<
  TestBody["commercialProductProfile"]
>;

function TestQuickFacts({
  items,
  extraFacts = [],
}: {
  items: TestQuickFact[];
  /** Facts the sheet adds after the content's own, such as Where. */
  extraFacts?: TestGlanceFact[];
}) {
  const t = useTranslations();
  return (
    <FactGrid
      facts={[...testQuickFactsAsGlanceFacts(t, items), ...extraFacts]}
      style={{ marginTop: 10 }}
    />
  );
}

function CommercialProductProfileRows({
  profile,
}: {
  profile: CommercialProductProfile;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CommercialProductProfileRows");
  const { locale } = useLocale();
  const hasNames = Boolean(profile.marketedNames?.length);
  const hasAvailability = Boolean(profile.availability?.length);

  if (!hasNames && !hasAvailability && !profile.productUrl) return null;

  const rows = [
    ...(hasNames
      ? [
          {
            key: "marketed-names",
            icon: Tags,
            label: t("learn.test_detail.marketed_as"),
            detail: profile.marketedNames!.join(", "),
            countries: undefined,
          },
        ]
      : []),
    ...(profile.availability ?? []).map((item, index) => ({
      key: `availability-${index}`,
      icon: Globe2,
      label: item.region,
      detail: item.detail,
      countries: item.countries,
    })),
  ];

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {rows.map((row) => {
        const Icon = row.icon;

        return (
          <View key={row.key} style={{ flexDirection: "row", gap: 10 }}>
            <Icon size={13} color={EMBER.colors.textFaint} strokeWidth={2} />
            <View style={{ flex: 1, gap: 3 }}>
              <Text className="font-dm-sans" style={MINI_LABEL_TEXT_STYLE}>
                {row.label}
              </Text>
              {row.countries?.length ? (
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 6,
                    marginTop: 2,
                    marginBottom: 3,
                  }}
                >
                  {row.countries.map((country) => {
                    const flag = flagFromIso(country);
                    return (
                      <Tag
                        key={country}
                        shape="pill"
                        label={getCountryLabel(country, locale)}
                        leading={
                          flag ? (
                            <Text
                              className="font-dm-sans"
                              style={{ fontSize: 12, lineHeight: 14 }}
                            >
                              {flag}
                            </Text>
                          ) : undefined
                        }
                      />
                    );
                  })}
                </View>
              ) : null}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  color: EMBER.colors.textLight,
                  lineHeight: 18,
                }}
              >
                {row.detail}
              </Text>
            </View>
          </View>
        );
      })}

      {profile.productUrl ? (
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel("Official product page"),
              "open_product_page",
            );
            Linking.openURL(profile.productUrl!).catch(() => {});
          }}
          style={({ pressed }) => ({
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            opacity: pressed ? 0.6 : 1,
          })}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <ExternalLink
              size={13}
              color={EMBER.colors.textMid}
              strokeWidth={2}
            />
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 13,
                fontWeight: "700",
                color: EMBER.colors.textMid,
                lineHeight: 18,
              }}
            >
              Official product page
            </Text>
          </View>
          <ChevronRight
            size={16}
            color={EMBER.colors.textFaint}
            strokeWidth={2}
          />
        </Pressable>
      ) : null}
    </View>
  );
}

function CommercialProductProfileSection({
  profile,
}: {
  profile: CommercialProductProfile;
}) {
  const t = useTranslations();
  const hasContent = Boolean(
    profile.marketedNames?.length ||
    profile.availability?.length ||
    profile.productUrl,
  );

  if (!hasContent) return null;

  return (
    <Accordion type="single" collapsible style={{ marginTop: 24 }}>
      <AccordionItem value="available-as">
        <AccordionTrigger
          accessibilityLabel={t("learn.test_detail.section_available_as")}
          action={{
            expand: "expand_available_as",
            collapse: "collapse_available_as",
          }}
          className="items-center py-0"
        >
          <SectionTitle icon={Globe2}>
            {t("learn.test_detail.section_available_as")}
          </SectionTitle>
        </AccordionTrigger>
        <AccordionContent className="pb-0">
          <CommercialProductProfileRows profile={profile} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function IconTextRows({
  items,
  tone,
}: {
  items: string[];
  tone: "positive" | "caution";
}) {
  if (!items.length) return null;

  const Icon = tone === "positive" ? CheckCircle : XCircle;
  const iconColor =
    tone === "positive"
      ? semanticColors.status.success
      : semanticColors.status.danger;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((item, i) => (
        <View key={`${item}-${i}`} style={{ flexDirection: "row", gap: 10 }}>
          <Icon
            size={13}
            color={iconColor}
            strokeWidth={2}
            style={{ marginTop: 2.5 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function PlainTextRows({
  items,
  icon: Icon,
}: {
  items: string[];
  icon: typeof AlertCircle;
}) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((item, i) => (
        <View key={`${item}-${i}`} style={{ flexDirection: "row", gap: 10 }}>
          <Icon
            size={13}
            color={EMBER.colors.textLight}
            strokeWidth={2}
            style={{ marginTop: 2.5 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function BestForRows({ items }: { items: string[] }) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 10, marginTop: 10 }}>
      {items.map((item, i) => (
        <View
          key={`${item}-${i}`}
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 10,
            width: "100%",
          }}
        >
          <Target
            size={12}
            color={EMBER.colors.textLight}
            strokeWidth={2}
            style={{ marginTop: 3 }}
          />
          <Text
            className="font-dm-sans"
            style={{
              flex: 1,
              flexShrink: 1,
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function MarkerTimingRows({
  items,
}: {
  items: NonNullable<TestBody["markerTiming"]>;
}) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 14, marginTop: 10 }}>
      {items.map((item, i) => (
        <View
          key={`${item.marker}-${i}`}
          style={{ flexDirection: "row", gap: 12 }}
        >
          <CalendarDays
            size={15}
            color={EMBER.colors.textMid}
            strokeWidth={2}
            style={{ marginTop: 1 }}
          />
          <View style={{ flex: 1, gap: 3 }}>
            <SectionTitle level="subsection">{item.marker}</SectionTitle>
            <CardSubtitleText style={{ marginTop: 0 }}>
              {item.timing}
            </CardSubtitleText>
            <MetaText style={{ marginTop: 0 }}>{item.reason}</MetaText>
          </View>
        </View>
      ))}
    </View>
  );
}

const RESULT_GLYPH_COLORS = {
  walnut: "#8A6F5B",
  taupe: "#B99A83",
  gold: "#D9A441",
  copper: "#C87952",
  clay: "#E0A98C",
  pale: "#D8D1C8",
  faint: "rgba(184,166,149,0.38)",
  ink: "#6F5949",
};

function ResultGlyphPart({ style }: { style: ViewStyle }) {
  return (
    <View
      style={[
        {
          position: "absolute",
          borderRadius: 999,
        },
        style,
      ]}
    />
  );
}

function UnknownResultGlyph() {
  return (
    <View
      style={{
        width: 42,
        height: 42,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <ResultGlyphPart
        style={{
          width: 28,
          height: 28,
          borderWidth: 2,
          borderColor: RESULT_GLYPH_COLORS.pale,
          backgroundColor: "transparent",
        }}
      />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          fontWeight: "800",
          color: RESULT_GLYPH_COLORS.taupe,
          lineHeight: 24,
        }}
      >
        ?
      </Text>
    </View>
  );
}

function WindowResultGlyph({ kind }: { kind: ResultGlyphKind }) {
  const activeIndex =
    kind === "window-pre" ? 0 : kind === "window-post" ? 2 : 1;
  const activeColor =
    kind === "window-receptive"
      ? RESULT_GLYPH_COLORS.gold
      : RESULT_GLYPH_COLORS.copper;

  return (
    <View style={{ width: 42, height: 42 }}>
      <ResultGlyphPart
        style={{
          left: 6,
          top: 31,
          width: 30,
          height: 3,
          backgroundColor: RESULT_GLYPH_COLORS.faint,
        }}
      />
      {[0, 1, 2].map((index) => {
        const isActive = index === activeIndex;
        return (
          <ResultGlyphPart
            key={index}
            style={{
              left: 9 + index * 10,
              top: isActive ? 8 : 14,
              width: 6,
              height: isActive ? 24 : 18,
              backgroundColor: isActive
                ? activeColor
                : RESULT_GLYPH_COLORS.pale,
            }}
          />
        );
      })}
      <ResultGlyphPart
        style={{
          left: 17,
          top: 5,
          width: 8,
          height: 8,
          backgroundColor:
            kind === "window-receptive"
              ? RESULT_GLYPH_COLORS.clay
              : RESULT_GLYPH_COLORS.faint,
        }}
      />
    </View>
  );
}

function MicrobiomeResultGlyph({ kind }: { kind: ResultGlyphKind }) {
  const isDominant = kind === "microbiome-dominant";
  const isPathogen = kind === "microbiome-pathogen";
  const isLow = kind === "microbiome-low";
  const rods = [
    {
      left: 8,
      top: 10,
      width: 15,
      height: 5,
      rotate: "-22deg",
      color: isPathogen ? RESULT_GLYPH_COLORS.copper : RESULT_GLYPH_COLORS.gold,
      opacity: isLow ? 0.42 : 1,
    },
    {
      left: 21,
      top: 8,
      width: 12,
      height: 5,
      rotate: "28deg",
      color: isDominant
        ? RESULT_GLYPH_COLORS.taupe
        : RESULT_GLYPH_COLORS.copper,
      opacity: isLow ? 0.32 : 1,
    },
    {
      left: 11,
      top: 23,
      width: 13,
      height: 5,
      rotate: "18deg",
      color: isDominant ? RESULT_GLYPH_COLORS.taupe : RESULT_GLYPH_COLORS.clay,
      opacity: isLow ? 0.28 : 1,
    },
    {
      left: 25,
      top: 25,
      width: 9,
      height: 5,
      rotate: "-34deg",
      color: isDominant ? RESULT_GLYPH_COLORS.gold : RESULT_GLYPH_COLORS.walnut,
      opacity: isLow ? 0 : 1,
    },
  ];

  return (
    <View style={{ width: 42, height: 42 }}>
      {rods.map((rod, index) =>
        rod.opacity === 0 ? null : (
          <ResultGlyphPart
            key={index}
            style={{
              left: rod.left,
              top: rod.top,
              width: rod.width,
              height: rod.height,
              backgroundColor: rod.color,
              opacity: rod.opacity,
              transform: [{ rotate: rod.rotate }],
            }}
          />
        ),
      )}
      <ResultGlyphPart
        style={{
          left: 15,
          top: 16,
          width: 6,
          height: 6,
          backgroundColor: isPathogen
            ? RESULT_GLYPH_COLORS.copper
            : RESULT_GLYPH_COLORS.pale,
          opacity: isLow ? 0.35 : 1,
        }}
      />
      {isLow ? (
        <Text
          className="font-dm-sans"
          style={{
            position: "absolute",
            right: 7,
            bottom: 4,
            fontSize: 17,
            fontWeight: "800",
            color: RESULT_GLYPH_COLORS.taupe,
            lineHeight: 20,
          }}
        >
          ?
        </Text>
      ) : null}
    </View>
  );
}

function DnaMini({
  left,
  top = 5,
  colorA,
  colorB,
  scale = 1,
}: {
  left: number;
  top?: number;
  colorA: string;
  colorB: string;
  scale?: number;
}) {
  const dotSize = 4 * scale;
  return (
    <>
      {[0, 1, 2, 3].map((index) => (
        <View key={index}>
          <ResultGlyphPart
            style={{
              left: left + (index % 2) * 9 * scale,
              top: top + index * 7 * scale,
              width: dotSize,
              height: dotSize,
              backgroundColor: index % 2 === 0 ? colorA : colorB,
            }}
          />
          <ResultGlyphPart
            style={{
              left: left + 10 * scale - (index % 2) * 9 * scale,
              top: top + index * 7 * scale,
              width: dotSize,
              height: dotSize,
              backgroundColor: index % 2 === 0 ? colorB : colorA,
            }}
          />
          <ResultGlyphPart
            style={{
              left: left + 3 * scale,
              top: top + 1.5 * scale + index * 7 * scale,
              width: 11 * scale,
              height: 1.8 * scale,
              backgroundColor: RESULT_GLYPH_COLORS.faint,
              transform: [{ rotate: index % 2 === 0 ? "18deg" : "-18deg" }],
            }}
          />
        </View>
      ))}
    </>
  );
}

function GeneticResultGlyph({ kind }: { kind: ResultGlyphKind }) {
  if (kind === "genetic-pair-risk") {
    return (
      <View style={{ width: 42, height: 42 }}>
        <DnaMini
          left={5}
          colorA={RESULT_GLYPH_COLORS.copper}
          colorB={RESULT_GLYPH_COLORS.gold}
          scale={0.82}
        />
        <DnaMini
          left={22}
          colorA={RESULT_GLYPH_COLORS.copper}
          colorB={RESULT_GLYPH_COLORS.gold}
          scale={0.82}
        />
      </View>
    );
  }

  const colorA =
    kind === "genetic-affected"
      ? RESULT_GLYPH_COLORS.copper
      : RESULT_GLYPH_COLORS.gold;
  const colorB =
    kind === "genetic-clear"
      ? RESULT_GLYPH_COLORS.taupe
      : RESULT_GLYPH_COLORS.copper;

  return (
    <View style={{ width: 42, height: 42 }}>
      <DnaMini left={11} colorA={colorA} colorB={colorB} scale={1.1} />
    </View>
  );
}

function ChromosomeX({
  left,
  top,
  color,
  shortArm,
  opacity = 1,
}: {
  left: number;
  top: number;
  color: string;
  shortArm?: boolean;
  opacity?: number;
}) {
  return (
    <>
      <ResultGlyphPart
        style={{
          left,
          top,
          width: shortArm ? 4 : 5,
          height: 24,
          backgroundColor: color,
          opacity,
          transform: [{ rotate: "24deg" }],
        }}
      />
      <ResultGlyphPart
        style={{
          left: left + 8,
          top: top + (shortArm ? 5 : 0),
          width: 5,
          height: shortArm ? 16 : 24,
          backgroundColor: color,
          opacity,
          transform: [{ rotate: "-24deg" }],
        }}
      />
    </>
  );
}

function ChromosomeResultGlyph({ kind }: { kind: ResultGlyphKind }) {
  if (kind === "chromosome-mosaic") {
    return (
      <View style={{ width: 42, height: 42 }}>
        <ChromosomeX
          left={7}
          top={9}
          color={RESULT_GLYPH_COLORS.taupe}
          opacity={0.9}
        />
        <ChromosomeX
          left={21}
          top={9}
          color={RESULT_GLYPH_COLORS.copper}
          opacity={0.9}
        />
      </View>
    );
  }

  const color =
    kind === "chromosome-unbalanced"
      ? RESULT_GLYPH_COLORS.copper
      : kind === "chromosome-balanced"
        ? RESULT_GLYPH_COLORS.gold
        : RESULT_GLYPH_COLORS.taupe;

  return (
    <View style={{ width: 42, height: 42 }}>
      <ChromosomeX
        left={8}
        top={9}
        color={color}
        shortArm={kind === "chromosome-unbalanced"}
      />
      <ChromosomeX
        left={22}
        top={9}
        color={
          kind === "chromosome-balanced" ? RESULT_GLYPH_COLORS.taupe : color
        }
      />
    </View>
  );
}

function ResultTypeGlyph({ kind }: { kind: ResultGlyphKind }) {
  if (kind.startsWith("window-")) return <WindowResultGlyph kind={kind} />;
  if (kind.startsWith("microbiome-")) {
    return <MicrobiomeResultGlyph kind={kind} />;
  }
  if (kind.startsWith("genetic-")) return <GeneticResultGlyph kind={kind} />;
  if (kind.startsWith("chromosome-")) {
    return <ChromosomeResultGlyph kind={kind} />;
  }
  return <UnknownResultGlyph />;
}

function ResultTypeRows({
  items,
  illustrations,
  glyphs,
}: {
  items: NonNullable<TestBody["resultTypes"]>;
  illustrations?: Partial<Record<string, ImageSourcePropType>>;
  glyphs?: Partial<Record<string, ResultGlyphKind>>;
}) {
  if (!items.length) return null;

  return (
    <View style={{ gap: 16, marginTop: 10 }}>
      {items.map((item) => {
        const illustration = illustrations?.[item.label];
        const glyph = glyphs?.[item.label];
        const hasVisual = Boolean(illustration || glyph);

        return (
          <View key={item.label}>
            <View
              style={{
                gap: 7,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: hasVisual ? 12 : 0,
                }}
              >
                {illustration ? (
                  <Image
                    source={illustration}
                    style={{
                      width: 42,
                      height: 42,
                    }}
                    resizeMode="contain"
                  />
                ) : glyph ? (
                  <ResultTypeGlyph kind={glyph} />
                ) : null}
                <View style={{ flex: 1, gap: 3 }}>
                  <SectionTitle level="subsection">{item.label}</SectionTitle>
                  <CardSubtitleText style={{ marginTop: 0 }}>
                    {item.meaning}
                  </CardSubtitleText>
                </View>
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  color: EMBER.colors.textFaint,
                  lineHeight: 17,
                }}
              >
                {item.whatToExpect}
              </Text>
            </View>
          </View>
        );
      })}
    </View>
  );
}

function SoldAsRows({
  items,
  onOpenFactSheet,
}: {
  items: NonNullable<TestBody["soldAs"]>;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("SoldAsRows");
  if (!items.length) return null;

  return (
    <View style={{ gap: 12, marginTop: 10 }}>
      {items.map((item) => {
        const canOpen = Boolean(item.linkedTestId && onOpenFactSheet);
        const content = (
          <>
            <View style={{ gap: 11 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 6,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 14,
                    fontWeight: "800",
                    color: EMBER.colors.textMid,
                    lineHeight: 18,
                  }}
                >
                  {item.name}
                </Text>
                {item.company ? (
                  <Tag label={item.company} shape="pill" />
                ) : null}
              </View>
              <MoonDetailRow
                icon={Info}
                label={t("learn.test_detail.what_it_is")}
                value={
                  <CardSubtitleText style={{ marginTop: 0 }}>
                    {item.description}
                  </CardSubtitleText>
                }
              />
              {item.evidenceSummary ? (
                <MoonDetailRow
                  icon={BookOpen}
                  label={t("learn.test_detail.evidence_note")}
                  value={
                    <CardSubtitleText style={{ marginTop: 0 }}>
                      {item.evidenceSummary}
                    </CardSubtitleText>
                  }
                />
              ) : null}
            </View>
            {canOpen ? (
              <View
                style={{
                  marginTop: 12,
                  paddingTop: 10,
                  borderTopWidth: 1,
                  borderTopColor: semanticColors.border.subtle,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    fontWeight: "800",
                    color: EMBER.colors.textMid,
                    lineHeight: 14,
                  }}
                >
                  View product evidence
                </Text>
                <ChevronRight
                  size={16}
                  color={EMBER.colors.textFaint}
                  strokeWidth={2}
                />
              </View>
            ) : null}
          </>
        );

        // Each product keeps the shared panel: it carries several fields and
        // its own evidence link, and without an edge one product's fields run
        // into the next.
        const sharedStyle = {
          ...cardSurface({ variant: "panel" }),
          paddingHorizontal: 12,
          paddingVertical: 14,
        };

        return canOpen ? (
          <Pressable
            key={item.name}
            onPress={() => {
              capturePress(pressLabel(item.name), "open_sold_as_test");
              onOpenFactSheet?.({
                kind: "test",
                id: item.linkedTestId as string,
              });
            }}
            style={({ pressed }) => ({
              ...sharedStyle,
              ...(pressed && {
                backgroundColor: semanticColors.overlay.pressed,
              }),
            })}
          >
            {content}
          </Pressable>
        ) : (
          <View key={item.name} style={sharedStyle}>
            {content}
          </View>
        );
      })}
    </View>
  );
}

function TestDataPointRows({
  dataPointIds,
  onOpenMetric,
}: {
  dataPointIds: ContentBlockId[];
  onOpenMetric?: (metricKey: string) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("TestDataPointRows");
  const dataPoints = dataPointIds
    .map((id) => getBlock(id))
    .filter(
      (block): block is ContentBlock<"data-point"> =>
        block !== undefined && block.kind === "data-point",
    );
  if (!dataPoints.length) return null;

  const rowBaseStyle = {
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 12,
  };

  return (
    <View style={{ marginTop: 24 }}>
      <SectionTitle icon={Beaker}>
        {t("learn.test_detail.section_biomarkers_tracked")}
      </SectionTitle>
      <View style={{ marginTop: 10, gap: 14 }}>
        {dataPoints.map((dataPoint) => {
          const body = dataPoint.body as DataPointBody;
          const canOpen = Boolean(body.metricKey && onOpenMetric);
          const content = (
            <>
              <Beaker size={16} color={EMBER.colors.textMid} />
              <View style={{ flex: 1, gap: 6 }}>
                <SectionTitle level="subsection">
                  {dataPoint.title}
                </SectionTitle>
                {body.interpretation ? (
                  <CardSubtitleText numberOfLines={2} style={{ marginTop: 0 }}>
                    {body.interpretation}
                  </CardSubtitleText>
                ) : null}
                {body.sampleTypes?.length || body.unit ? (
                  <MetaText>
                    {dataPointMetaLine(t, body.sampleTypes, body.unit)}
                  </MetaText>
                ) : null}
              </View>
              {canOpen ? (
                <ChevronRight size={16} color={EMBER.colors.textFaint} />
              ) : null}
            </>
          );

          return canOpen ? (
            <Pressable
              key={dataPoint.id}
              onPress={() => {
                capturePress(pressLabel(dataPoint.title), "open_metric");
                onOpenMetric?.(body.metricKey!);
              }}
              style={({ pressed }) => ({
                ...rowBaseStyle,
                opacity: pressed ? 0.6 : 1,
              })}
            >
              {content}
            </Pressable>
          ) : (
            <View key={dataPoint.id} style={rowBaseStyle}>
              {content}
            </View>
          );
        })}
      </View>
    </View>
  );
}

/* ─── Test detail sections (existing layout) ─── */

function TestSections({
  id,
  body,
  sources,
  onEvidenceLevelPress,
  onOpenMetric,
  onOpenFactSheet,
  glanceFacts,
}: {
  id: ContentBlockId;
  body: TestBody;
  sources?: ContentBlock["sources"];
  onEvidenceLevelPress: (level: string) => void;
  onOpenMetric?: (metricKey: string) => void;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
  /** Where the test is done and who sells it, after the quick facts. */
  glanceFacts: TestGlanceFact[];
}) {
  const t = useTranslations();
  const howItWorksSteps =
    body.howItWorksSteps ?? splitTestSteps(body.howItWorks);
  const showCycleDaySection = Boolean(body.cycleDay && !body.howItWorksSteps);
  const hasResultsQuickFact =
    body.quickFacts?.some((fact) => fact.kind === "results") ?? false;
  const considerationItems = [
    ...(body.expectations?.shouldNotExpect ?? []),
    ...(body.considerations ?? []),
  ];
  return (
    <>
      <View style={{ marginTop: 28 }}>
        <SectionTitle icon={Heart}>
          {t("learn.test_detail.section_what_it_measures")}
        </SectionTitle>
        <ReadableLinkedFactSheetText
          text={body.whatItMeasures}
          currentBlockId={id}
          onOpenFactSheet={onOpenFactSheet}
          style={{
            fontSize: 14,
            color: EMBER.colors.textLight,
            lineHeight: 22,
          }}
        />
      </View>

      {body.quickFacts?.length || glanceFacts.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Info}>
            {t("learn.test_detail.section_at_a_glance")}
          </SectionTitle>
          <TestQuickFacts
            items={body.quickFacts ?? []}
            extraFacts={glanceFacts}
          />
        </View>
      ) : null}

      {body.soldAs?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={ExternalLink}>
            {t("learn.test_detail.section_sold_as")}
          </SectionTitle>
          <SoldAsRows items={body.soldAs} onOpenFactSheet={onOpenFactSheet} />
        </View>
      ) : null}

      {body.resultTypes?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={FileText}>
            {t("learn.test_detail.section_result_types")}
          </SectionTitle>
          <ResultTypeRows
            items={body.resultTypes}
            illustrations={TEST_RESULT_TYPE_ILLUSTRATIONS[id]}
            glyphs={TEST_RESULT_TYPE_GLYPHS[id]}
          />
        </View>
      ) : null}

      <TestDataPointRows
        dataPointIds={getDataPointsForTest(id).map((dataPoint) => dataPoint.id)}
        onOpenMetric={onOpenMetric}
      />

      {body.markerTiming?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={CalendarDays}>
            {t("learn.test_detail.section_marker_timing")}
          </SectionTitle>
          <MarkerTimingRows items={body.markerTiming} />
        </View>
      ) : null}

      <View style={{ marginTop: 24 }}>
        <SectionTitle icon={Wrench}>
          {t("learn.test_detail.section_how_it_works")}
        </SectionTitle>
        <TestStepRows
          items={howItWorksSteps}
          currentBlockId={id}
          onOpenFactSheet={onOpenFactSheet}
        />
      </View>

      {body.bestFor?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Target}>
            {t("learn.test_detail.section_best_for")}
          </SectionTitle>
          <BestForRows items={body.bestFor} />
        </View>
      ) : null}

      {body.keyFindings?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Beaker}>
            {t("learn.test_detail.section_research")}
          </SectionTitle>
          <ResearchFindingRows items={body.keyFindings} />
        </View>
      ) : null}

      {body.expectations?.canExpect.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Lightbulb}>
            {t("learn.test_detail.section_what_to_expect")}
          </SectionTitle>
          <IconTextRows items={body.expectations.canExpect} tone="positive" />
        </View>
      ) : null}

      {considerationItems.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={AlertCircle}>
            {t("learn.test_detail.section_considerations")}
          </SectionTitle>
          <PlainTextRows items={considerationItems} icon={AlertCircle} />
        </View>
      ) : null}

      {body.whyItMatters?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Lightbulb}>
            {t("learn.test_detail.section_why_it_matters")}
          </SectionTitle>
          <BenefitRows items={body.whyItMatters} />
        </View>
      ) : null}

      {body.turnaroundTime && !hasResultsQuickFact && (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Clock}>
            {t("learn.test_detail.section_turnaround_time")}
          </SectionTitle>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              lineHeight: 22,
              marginTop: 10,
            }}
          >
            {body.turnaroundTime}
          </Text>
        </View>
      )}

      {sources?.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={BookOpen}>
            {t("learn.test_detail.section_evidence")}
          </SectionTitle>
          <EvidenceSourceRows
            sources={sources}
            onEvidenceLevelPress={onEvidenceLevelPress}
            style={{ marginTop: 10 }}
          />
        </View>
      ) : null}

      {showCycleDaySection ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={CalendarDays}>
            {t("learn.test_detail.section_when_to_take")}
          </SectionTitle>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              lineHeight: 22,
              marginTop: 10,
            }}
          >
            {body.cycleDay}
          </Text>
        </View>
      ) : null}

      {body.commercialProductProfile ? (
        <CommercialProductProfileSection
          profile={body.commercialProductProfile}
        />
      ) : null}
    </>
  );
}

/* ─── Product detail sections ─── */

function ProductSections({
  body,
  productId,
}: {
  body: ProductBody;
  productId: ContentBlockId;
}) {
  const t = useTranslations();
  const includedDataPoints = getDataPointsForProduct(productId);
  return (
    <>
      <View style={{ marginTop: 28 }}>
        <SectionTitle icon={Info}>
          {t("learn.test_detail.section_about_product")}
        </SectionTitle>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: EMBER.colors.textLight,
            lineHeight: 22,
            marginTop: 10,
          }}
        >
          {body.description}
        </Text>
      </View>

      {includedDataPoints.length ? (
        <View style={{ marginTop: 24 }}>
          <SectionTitle icon={Beaker}>
            {t("learn.test_detail.section_panel_parameters")}
          </SectionTitle>
          <View style={{ gap: 8, marginTop: 10 }}>
            {includedDataPoints.map((dataPoint) => (
              <Text
                key={dataPoint.id}
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  color: EMBER.colors.textLight,
                  lineHeight: 21,
                }}
              >
                • {dataPoint.title}
              </Text>
            ))}
          </View>
        </View>
      ) : null}

      <View style={{ marginTop: 24 }}>
        <SectionTitle icon={DollarSign}>
          {t("learn.test_detail.section_price")}
        </SectionTitle>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            marginTop: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            ${body.price}
          </Text>
          {body.originalPrice != null && (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 14,
                color: EMBER.colors.textFaint,
                textDecorationLine: "line-through",
              }}
            >
              ${body.originalPrice}
            </Text>
          )}
        </View>
      </View>

      {body.orderUrl ? (
        <PrimaryButton
          label={t("learn.test_detail.order_from_provider")}
          action="learn.test_detail.order_from_provider"
          onPress={() => Linking.openURL(body.orderUrl!).catch(() => {})}
          leading={
            <ExternalLink
              size={16}
              color={semanticColors.action.primary.foreground}
              strokeWidth={2}
            />
          }
          style={{ marginTop: 28 }}
        />
      ) : (
        <OrderKitButton productId={productId} />
      )}
    </>
  );
}

/* ─── Modal Wrapper ─── */

type Props = {
  item: ContentBlock<"test"> | ContentBlock<"product"> | null;
  onClose?: () => void;
  returnTab?: keyof MainTabParamList;
};

export default function TestDetailSheet({ item, onClose, returnTab }: Props) {
  if (!item) return null;

  return (
    <CanonicalTestRouteLauncher
      item={item}
      onClose={onClose}
      returnTab={returnTab}
    />
  );
}

function CanonicalTestRouteLauncher({
  item,
  onClose,
  returnTab,
}: {
  item: NonNullable<Props["item"]>;
  onClose?: () => void;
  returnTab?: keyof MainTabParamList;
}) {
  const navigation = useNavigation();
  const target = useMemo(() => getTestRouteTarget(String(item.id)), [item.id]);

  useEffect(() => {
    onClose?.();
    navigation.dispatch(
      CommonActions.navigate({
        name: "Access",
        params: {
          screen: target.screen,
          params: returnTab ? { ...target.params, returnTab } : target.params,
        },
      }),
    );
  }, [navigation, onClose, returnTab, target]);

  return null;
}
